
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  MapPin,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  Search,
  SlidersHorizontal,
  ChevronDown,
  User,
  X,
  Menu
} from "lucide-react";
import { Dropdown, Image, message } from "antd";
import { LogoutOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../config/config";
import "./home.css"
import PortfolioOverview from "../portfolioOverview/PortfolioOverview";
import ScrollReveal from "../../../common/scrollReveal/ScrollReveal";
import ProfileModal from "../../../components/profileModal/ProfileModal";
import PortfolioOverviewComp from "../../../components/portfolioOverviewComp/PortfolioOverviewComp";

const PropertyCard = ({ property, onViewInvestment, activeTab,onSeeDetails }) => {
  const totalBlocks = Number(property?.total_tokens || 0);
  const blocksSold = Number(property?.blocks_sold || 0);

  const availableBlocks = Math.max(totalBlocks - blocksSold, 0);

  const fundedPercentage = Math.min(
    Math.max(
      Number(
        property?.funding_pct ??
          (totalBlocks ? (blocksSold / totalBlocks) * 100 : 0)
      ),
      0
    ),
    100
  );

  const imagePath = property?.image_paths?.[0];

  const imageUrl = imagePath
    ? `${BASE_URL}/uploads/${imagePath}`
    : null;

  const propertyType =
    property?.property_type?.replace(/_/g, " ") || "Property";

  const location = property?.location || "Location unavailable";

  const status = property?.status || property?.submission_status || "Open";

  const statusText = status
    .toString()
    .replace(/_/g, " ")
    .toLowerCase();

  const isClosingSoon = statusText.includes("closing");

  return (
    <div
      className="
        group relative overflow-hidden
        rounded-[18px]
        border border-[#e7e5d8]
        bg-[#fffdf8]/95
        shadow-[0_8px_24px_rgba(55,92,32,0.08)]
        backdrop-blur-xl
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#cbd8bb]
        hover:shadow-[0_18px_38px_rgba(55,92,32,0.15)]
      "
    >
      <div
        className="
          pointer-events-none absolute
          -right-12 -top-12
          h-28 w-28
          rounded-full
          bg-[#e3ecd6]/70
          blur-[35px]
        "
      />

      <div className="relative m-2 overflow-hidden rounded-[13px]">
        <div className="h-[165px] w-full overflow-hidden bg-[#f1eee4]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={property?.name || "Property"}
              className="
                h-full w-full
                object-cover
                transition-transform duration-500
                group-hover:scale-[1.06]
              "
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7b95a5]">
              No image available
            </div>
          )}
        </div>

        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2">
          <span
            className="
              rounded-md
              border border-white/60
              bg-[#375c20]/90
              px-2.5 py-1
              text-[10px]
              font-semibold
              capitalize
              tracking-wide
              text-white
              shadow-sm
              backdrop-blur-md
            "
          >
            {propertyType}
          </span>

          <span
            className="
              rounded-md
              border border-white/70
              bg-[#fffdf8]/95
              px-2.5 py-1
              text-[10px]
              font-bold
              text-[#4c7a2e]
              shadow-sm
            "
          >
            {property?.gross_annual_yield_pct ?? 0}% yield
          </span>
        </div>
      </div>

      <div className="px-3 pb-3">

        <div className="mb-1 flex items-center gap-1.5">
          <svg
            className="h-3 w-3 shrink-0 text-[#6a7847]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z"
            />
            <circle cx="12" cy="9" r="2.2" />
          </svg>

          <span
            className="
              truncate
              text-[13px]
              font-medium
              text-[#6b6d5d]
            "
            title={location}
          >
            {location}
          </span>
        </div>

        <h3
          className="
            truncate
            text-[20px]
            font-bold
            leading-tight
            text-[#20231a]
          "
          title={property?.name}
        >
          {property?.name || "Unnamed Property"}
        </h3>

        <p className="mt-0.5 text-[12px] capitalize text-[#7b7d6d]">
          {propertyType}
        </p>

        <p
          className="
            mt-1.5
            line-clamp-1
            text-[12px]
            leading-4
            text-[#777967]
          "
        >
          {property?.description || "Premium investment opportunity"}
        </p>

        <div
          className="
            mt-2.5
            grid grid-cols-3
            divide-x divide-[#e0e2d7]
            rounded-[10px]
            border border-[#e2e3d9]
            bg-[#f7f4eb]
            px-1 py-2
          "
        >
          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Property value
            </p>

            <p className="mt-0.5 truncate text-[14px] font-bold text-[#20231a]">
              AED {Number(property?.property_value_aed || 0).toLocaleString()}
            </p>
          </div>

          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Min. investment
            </p>

            <p className="mt-0.5 truncate text-[14px] font-bold text-[#20231a]">
              AED
              {Number(
                property?.price_per_block_aed || 0
              ).toLocaleString()}
            </p>
          </div>

          <div className="px-2">
            <p className="text-[11px] font-medium text-[#7b7d6d]">
              Target yield
            </p>

            <p className="mt-0.5 text-[14px] font-bold text-[#24719a]">
              {property?.gross_annual_yield_pct ?? 0}%
            </p>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div>
            <p className="text-[11px] text-[#858676]">
              Total Tokens
            </p>

            <p className="text-[14px] font-semibold text-[#4f5545]">
              {totalBlocks.toLocaleString()}
            </p>
          </div>

          <div className="text-right">
            <p className="text-[11px] text-[#858676]">
              Available
            </p>

            <p className="text-[14px] font-semibold text-[#4f5545]">
              {availableBlocks.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-medium text-[#5d604f]">
              {Math.round(fundedPercentage)}% funded
            </span>

            <span className="text-[11px] text-[#777967]">
              {blocksSold.toLocaleString()} sold
            </span>
          </div>

          <div className="h-[4px] overflow-hidden rounded-full bg-[#e5e7dc]">
            <div
              className="
                h-full
                rounded-full
                bg-gradient-to-r
                from-[#6fa142]
                to-[#375c20]
                transition-all duration-500
              "
              style={{
                width: `${fundedPercentage}%`,
              }}
            />
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between">

          <span
            className={`
              rounded-full
              px-2 py-1
              text-[11px]
              font-semibold
              capitalize
              ${
                isClosingSoon
                  ? "bg-[#f6eddc] text-[#8b6a34]"
                  : "bg-[#e3ecd6] text-[#4f6e39]"
              }
            `}
          >
            {statusText}
          </span>

          <button
            type="button"
            onClick={() => {
              if(activeTab === "my-properties"){
                onSeeDetails(property)
              }else{
                onViewInvestment(property)}
              }
            }
            className="
              rounded-full
              bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20]
              px-5 py-1.5
              text-[16px]
              font-semibold
              text-white
              shadow-[0_6px_14px_rgba(55,92,32,0.20)]
              transition-all duration-200
              hover:brightness-95
              hover:shadow-[0_9px_20px_rgba(55,92,32,0.28)]
              active:scale-95
            "
          >
            {activeTab === "my-properties" ? "See Details" : "Invest"}
          </button>
        </div>
      </div>
    </div>
  );
};
const ProfileItem = ({ label, value }) => {
  return (
    <div className="rounded-[8px] border border-[#e7e8e6] bg-[#fafaf8] p-3">
      <p className="text-[8px] font-medium uppercase tracking-wide text-[#9a9e9f]">
        {label}
      </p>

      <p className="mt-1 break-all text-[10px] font-semibold text-[#263b47]">
        {value !== null && value !== undefined && value !== ""
          ? String(value)
          : "-"}
      </p>
    </div>
  );
};

// const ProfileModal = ({ isOpen, onClose, profile, isLoading }) => {
//   if (!isOpen) return null;

//   const firstName =
//     profile?.first_name || profile?.firstname || profile?.firstName || "";

//   const lastName =
//     profile?.last_name || profile?.lastname || profile?.lastName || "";

//   const fullName =
//     profile?.name ||
//     profile?.full_name ||
//     profile?.fullName ||
//     `${firstName} ${lastName}`.trim();

//   const email = profile?.email || profile?.email_address || "";

//   const phone =
//     profile?.phone || profile?.phone_number || profile?.mobile || "";

//   const walletAddress = profile?.wallet_address || profile?.walletAddress || "";

//   const role = profile?.role || profile?.user_role || "Investor";

//   return (
//     <div
//       className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-[2px]"
//       onClick={onClose}
//     >
//       <div
//         className="w-full max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-2xl"
//         onClick={(e) => e.stopPropagation()}
//       >
//         <div className="flex items-center justify-between border-b border-[#e7e7e4] px-5 py-4">
//           <div>
//             <h2 className="text-[17px] font-semibold text-[#183542]">
//               My Profile
//             </h2>

//             <p className="mt-1 text-[9px] text-[#9a9e9f]">
//               Your account information
//             </p>
//           </div>

//           <button
//             type="button"
//             onClick={onClose}
//             className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f3f4f2] text-[#6f777a] transition hover:bg-[#e7e9e7]"
//           >
//             <X size={15} />
//           </button>
//         </div>

//         <div className="p-5">
//           {isLoading ? (
//             <div className="space-y-4">
//               <div className="flex items-center gap-3">
//                 <div className="h-12 w-12 animate-pulse rounded-full bg-gray-200" />

//                 <div className="flex-1 space-y-2">
//                   <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
//                   <div className="h-2 w-20 animate-pulse rounded bg-gray-200" />
//                 </div>
//               </div>

//               <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//                 <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
//                 <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
//                 <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
//                 <div className="h-[60px] animate-pulse rounded-lg bg-gray-200" />
//               </div>
//             </div>
//           ) : profile ? (
//             <>
//               <div className="mb-5 flex items-center gap-3 rounded-xl bg-[#f8f8f6] p-4">
//                 <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e9dfca] text-[14px] font-bold text-[#20485b]">
//                   {(
//                     `${firstName?.[0] || ""}${lastName?.[0] || ""}` ||
//                     fullName?.[0] ||
//                     "I"
//                   ).toUpperCase()}
//                 </div>

//                 <div className="min-w-0">
//                   <p className="truncate text-[13px] font-semibold text-[#1b3441]">
//                     {fullName || "Investor"}
//                   </p>

//                   <p className="mt-1 text-[9px] text-[#8a9295]">{role}</p>
//                 </div>
//               </div>

//               <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//                 <ProfileItem label="First Name" value={firstName} />

//                 <ProfileItem label="Last Name" value={lastName} />

//                 <ProfileItem label="Email" value={email} />

//                 <ProfileItem label="Phone" value={phone} />

//                 <div className="sm:col-span-2">
//                   <ProfileItem label="Wallet Address" value={walletAddress} />
//                 </div>

//                 <ProfileItem label="Role" value={role} />


//               </div>
//             </>
//           ) : (
//             <div className="py-8 text-center">
//               <User size={30} className="mx-auto text-[#20485b]" />

//               <p className="mt-3 text-[11px] font-medium text-[#526067]">
//                 Unable to load profile information.
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };


const Home = () => {
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("investments");

  const [search, setSearch] = useState("");

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const [profile, setProfile] = useState(null);

  const [isProfileLoading, setIsProfileLoading] = useState(false);
const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const getToken = () => {
    return localStorage.getItem("rwatoken");
  };

  const getAllProperties = async () => {
    try {
      setIsLoading(true);

      const token = getToken();

      const response = await axios.get(`${BASE_URL}/investor/properties/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setProperties(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.log("Error fetching investment properties:", error);
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };


  const getMyProperties = async () => {
    try {
      setIsLoading(true);

      const token = getToken();

      const response = await axios.get(
        `${BASE_URL}/investor/properties/list?mine=true`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setProperties(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.log("Error fetching my properties:", error);
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (activeTab === "my-properties") {
      getMyProperties();
    } else {
      getAllProperties();
    }
  }, [activeTab]);

 
  const getProfile = async () => {
    try {
      const token = getToken();

      const response = await axios.get(`${BASE_URL}/investor/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data) {
        setProfile(response?.data);
      }
    } catch (error) {
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      console.log("Error fetching investor profile:", error);

      setProfile(null);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleViewInvestment = (property) => {
    navigate(`/property-details/${property?.id}`, {
      state: {
        id: property?.id || "",
        userDetails : profile
      },
    });
  };
  const handleSeeDetails = (id)=>{
    // navigate("/customer/property/details")
    navigate("/customer/portfolio/details",
      {state: {
              id: id
            }})
  }

  const handleLogout = () => {
    console.log("Before:", localStorage.getItem("rwatoken"));

    localStorage.removeItem("rwatoken");
    localStorage.removeItem("userRole");

    console.log("After:", localStorage.getItem("rwatoken"));
    window.location.href = "/";
    navigate("/login", { replace: true });
  };

 
  const userMenu = {
    items: [
      {
        key: "profile",
        icon: <User size={14} />,
        label: "Profile",
      },
      {
        type: "divider",
      },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Sign Out",
        danger: true,
      },
    ],

    onClick: ({ key }) => {
      if (key === "profile") {
        setIsProfileLoading(true);
        setIsProfileModalOpen(true);
        getProfile();
      }

      if (key === "logout") {
        handleLogout();
      }
    },
  };

  useEffect(() => {
    getProfile();
  }, []);


  const filteredProperties = properties.filter((property) => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) return true;

    return (
      property?.name?.toLowerCase().includes(searchValue) ||
      property?.location?.toLowerCase().includes(searchValue) ||
      property?.property_type?.toLowerCase().includes(searchValue) ||
      property?.description?.toLowerCase().includes(searchValue)
    );
  });
  const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=90";

  const propertyCategories = [
  {
    title: "Residential",
    opportunities: "43 opportunities",
    image:
      "https://images.unsplash.com/photo-1510798831971-661eb04b3739?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Commercial",
    opportunities: "21 opportunities",
    image:
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Luxury",
    opportunities: "16 opportunities",
    image:
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Vacation Homes",
    opportunities: "12 opportunities",
    image:
      "https://images.unsplash.com/photo-1449158743715-0a90ebb6d2d8?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Multifamily",
    opportunities: "27 opportunities",
    image:
      "https://images.unsplash.com/photo-1600607688969-a5bfcd646154?auto=format&fit=crop&w=900&q=85",
  },
  {
    title: "Development Projects",
    opportunities: "9 opportunities",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=900&q=85",
  },
];

const handleUpdateProfile = async (formData) => {
  const token = getToken();
  console.log(formData,"formdatatat")
  try {
    const response = await axios.patch(
      `${BASE_URL}/investor/me`,
      formData,
      {
        headers: {
          Authorization:  `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    console.log("Profile updated:", response.data);
    message.success("Profile Updated SuccessFully")
    // Refresh profile after successful update
    await getProfile();

  } catch (error) {
    if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
    console.log("Complete registration error:", error);
        message.error(error?.response?.data?.detail[0]?.msg || error?.response?.data?.detail ||
            "Something went wrong. Please try again."
        );
  }
};

return (
  <div className="relative min-h-screen overflow-hidden bg-[#fdfbf5] font-['Inter'] text-[#23241c]">

    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* Top left blue glow */}
      <div className="absolute -left-[180px] -top-[180px] h-[520px] w-[520px] rounded-full bg-[#dfe8cf]/70 blur-[120px]" />

      {/* Top right blue glow */}
      <div className="absolute -right-[180px] top-[80px] h-[500px] w-[500px] rounded-full bg-[#e8ecd9]/80 blur-[120px]" />

      {/* Center white glow */}
      <div className="absolute left-[35%] top-[25%] h-[500px] w-[500px] rounded-full bg-white/90 blur-[130px]" />

      {/* Bottom blue glow */}
      <div className="absolute bottom-[-250px] left-[20%] h-[550px] w-[550px] rounded-full bg-[#e5ead7]/60 blur-[130px]" />

      {/* Very subtle blue gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-[#fdfbf5]/80 to-[#f6f1e6]/70" />
    </div>


<header
  className="
    sticky top-0 z-30
    border-b border-[#fdfbf5]/80
    bg-white/75
    shadow-[0_4px_30px_rgba(55,92,32,0.07)]
    backdrop-blur-2xl
  "
>
  <div className="mx-auto flex h-[76px] w-full max-w-[1800px] items-center justify-between px-5 sm:px-7 lg:px-10">


    <div className="flex items-center gap-3">

      <div
        className="
          flex h-11 w-11
          items-center justify-center
          rounded-[13px]
          bg-gradient-to-br
          from-[#6fa142]
          via-[#4c7a2e]
          to-[#375c20]
          text-white
          shadow-[0_8px_20px_rgba(55,92,32,0.20)]
        "
      >
        <Building2 size={21} strokeWidth={1.8} />
      </div>

      <div>
        <div className="text-[18px] font-bold leading-none tracking-[-0.02em] text-[#1f2318] sm:text-[19px]">
          Property Investment
        </div>

        <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#6f705f]">
          Smart Real Estate Investing
        </div>
      </div>

    </div>


    <nav
      className="
        hidden
        items-center
        gap-2
        rounded-full
        border border-[#e9e8df]
        bg-white/70
        p-1.5
        shadow-sm
        backdrop-blur-md
        md:flex
      "
    >

      <button
        type="button"
        onClick={() => {
          setActiveTab("investments");
          setSearch("");
        }}
        className={`
          rounded-full
          px-5 py-2.5
          text-[13px]
          font-semibold
          transition-all
          duration-300
          ${
            activeTab === "investments"
              ? "bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-white shadow-[0_5px_15px_rgba(55,92,32,0.20)]"
              : "text-[#4a4b3f] hover:bg-[#f6f1e6] hover:text-[#4f5b36]"
          }
        `}
      >
        Investments
      </button>

      <button
        type="button"
        onClick={() => {
          setActiveTab("my-properties");
          setSearch("");
        }}
        className={`
          rounded-full
          px-5 py-2.5
          text-[13px]
          font-semibold
          transition-all
          duration-300
          ${
            activeTab === "my-properties"
              ? "bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-white shadow-[0_5px_15px_rgba(55,92,32,0.20)]"
              : "text-[#4a4b3f] hover:bg-[#f6f1e6] hover:text-[#4f5b36]"
          }
        `}
      >
        My Properties
      </button>

      <button
        type="button"
        onClick={() => {
          setActiveTab("portfolio");
          setSearch("");
        }}
        className={`
          rounded-full
          px-5 py-2.5
          text-[13px]
          font-semibold
          transition-all
          duration-300
          ${
            activeTab === "portfolio"
              ? "bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] text-white shadow-[0_5px_15px_rgba(55,92,32,0.20)]"
              : "text-[#4a4b3f] hover:bg-[#f6f1e6] hover:text-[#4f5b36]"
          }
        `}
      >
        Portfolio
      </button>

    </nav>


    <div className="flex items-center gap-3">


      <button
        type="button"
        onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        className="
          flex h-10 w-10
          items-center justify-center
          rounded-xl
          border border-[#e6e5db]
          bg-white/80
          text-[#4f5b36]
          shadow-[0_5px_15px_rgba(30,90,120,0.08)]
          transition-all
          duration-300
          hover:bg-[#f6f1e6]
          md:hidden
        "
        aria-label="Toggle navigation menu"
      >
        {isMobileMenuOpen ? (
          <X size={21} strokeWidth={2} />
        ) : (
          <Menu size={21} strokeWidth={2} />
        )}
      </button>


      <Dropdown
        menu={userMenu}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          className="
            flex h-10 w-10
            cursor-pointer
            items-center justify-center
            rounded-full
            border-2
            border-white
            bg-gradient-to-br
            from-[#e3ecd6]
            to-[#d7e3c6]
            text-[11px]
            font-bold
            text-[#4f5b36]
            shadow-[0_5px_18px_rgba(30,90,120,0.15)]
            transition-all
            duration-300
            hover:scale-105
            hover:shadow-[0_8px_22px_rgba(30,90,120,0.22)]
          "
        >
          {profile?.first_name?.[0]?.toUpperCase() ?? ""}
          {profile?.last_name?.[0]?.toUpperCase() ?? ""}
        </button>
      </Dropdown>

    </div>

  </div>


  {isMobileMenuOpen && (
    <div
      className="
        absolute
        left-0
        right-0
        top-[76px]
        border-t
        border-[#fdfbf5]/80
        bg-white/90
        px-5
        pb-5
        pt-4
        shadow-[0_15px_35px_rgba(20,70,100,0.12)]
        backdrop-blur-2xl
        md:hidden
      "
    >
      <div
        className="
          overflow-hidden
          rounded-2xl
          border border-[#e9e8df]
          bg-white/70
          p-2
        "
      >

        <button
          type="button"
          onClick={() => {
            setActiveTab("investments");
            setSearch("");
            setIsMobileMenuOpen(false);
          }}
          className={`
            flex w-full items-center
            rounded-xl
            px-4 py-3.5
            text-left
            text-[14px]
            font-semibold
            transition-all
            duration-300
            ${
              activeTab === "investments"
                ? "bg-[#4f5b36] text-white shadow-[0_5px_15px_rgba(23,76,106,0.15)]"
                : "text-[#4a4b3f] hover:bg-[#f6f1e6] hover:text-[#4f5b36]"
            }
          `}
        >
          Investments
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab("my-properties");
            setSearch("");
            setIsMobileMenuOpen(false);
          }}
          className={`
            mt-1
            flex w-full items-center
            rounded-xl
            px-4 py-3.5
            text-left
            text-[14px]
            font-semibold
            transition-all
            duration-300
            ${
              activeTab === "my-properties"
                ? "bg-[#4f5b36] text-white shadow-[0_5px_15px_rgba(23,76,106,0.15)]"
                : "text-[#4a4b3f] hover:bg-[#f6f1e6] hover:text-[#4f5b36]"
            }
          `}
        >
          My Properties
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab("portfolio");
            setSearch("");
            setIsMobileMenuOpen(false);
          }}
          className="
            mt-1
            flex w-full items-center
            rounded-xl
            px-4 py-3.5
            text-left
            text-[14px]
            font-semibold
            text-[#4a4b3f]
            transition-all
            duration-300
            hover:bg-[#f6f1e6]
            hover:text-[#4f5b36]
          "
        >
          Portfolio
        </button>

      </div>
    </div>
  )}

</header>
    {
      activeTab === "portfolio" ? 
      <PortfolioOverviewComp/>
      :
    <main className="relative mx-auto w-full max-w-[1800px] px-5 pb-20 pt-10 sm:px-7 lg:px-10">

      <section className="relative mb-12 overflow-hidden rounded-[34px] border border-[#e7e5d8] bg-[#f7f3e8]/80 px-5 py-7 shadow-[0_20px_70px_rgba(55,92,32,0.08)] backdrop-blur-2xl sm:px-8 sm:py-9 lg:px-12 lg:py-12">
        <div className="pointer-events-none absolute -left-28 -top-28 h-72 w-72 rounded-full bg-[#dfe8cf]/80 blur-[90px]" />
        <div className="pointer-events-none absolute -right-24 bottom-[-100px] h-80 w-80 rounded-full bg-[#e8ecd9]/80 blur-[100px]" />

        <div className="relative grid items-center gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:gap-14">
          <div className="max-w-[650px]">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#cfdabd] bg-white/75 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#4f5b36] shadow-sm backdrop-blur-md sm:text-[12px]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#6fa142] shadow-[0_0_10px_rgba(111,161,66,0.55)]" />
              {activeTab === "my-properties"
                ? "Your Portfolio"
                : "Fractional real estate, from AED 5000"}
            </span>

            <h1 className="mt-5 max-w-[650px] text-[38px] font-extrabold leading-[1.04] tracking-[-0.045em] text-[#20231a] sm:text-[48px] lg:text-[58px]">
              {activeTab === "my-properties" ? (
                <>
                  Invested in real estate.
                  <br />
                  <span className="text-[#4c7a2e] tracking-[-0.045em]">Track your future.</span>
                </>
              ) : (
                <>
                  Invest in real estate.
                  <br />
                  <span className="text-[#4c7a2e] tracking-[-0.045em]">Build your future.</span>
                </>
              )}
            </h1>

            <p className="mt-5 max-w-[610px] text-[14px] leading-7 text-[#626454] sm:text-[16px] sm:leading-8">
              {activeTab === "my-properties"
                ? "Track and explore the properties where you currently hold investments, monitor funding progress, and stay connected to your real-estate portfolio."
                : "The platform lets you own a share of vetted, income-producing properties — no mortgage, no property management, no six-figure minimum. Browse the opportunity, invest in minutes, and track your returns from one dashboard."}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#properties"
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-b from-[#6fa142] via-[#4c7a2e] to-[#375c20] px-6 py-3 text-[13px] font-bold text-white shadow-[0_10px_24px_rgba(55,92,32,0.22)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(55,92,32,0.28)]"
              >
                {activeTab === "my-properties" ? "Explore Portfolio" : "Explore Properties"}
                <span className="ml-2 text-[16px]">↗</span>
              </a>

              <a
                href="#properties"
                className="inline-flex items-center justify-center rounded-full border border-[#cfdabd] bg-white/70 px-6 py-3 text-[13px] font-bold text-[#4f5b36] shadow-sm backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-[#9fb78a] hover:bg-white"
              >
                View Opportunities
              </a>
            </div>

            <div className="mt-8 grid max-w-[610px] grid-cols-3 divide-x divide-[#d9ddcd] border-t border-[#dfe2d5] pt-6">
              <div className="pr-3 sm:pr-5">
                <b className="block text-[20px] font-extrabold tracking-[-0.03em] text-[#20231a] sm:text-[24px]">AED 142M+</b>
                <span className="mt-1 block text-[10px] font-medium leading-4 text-[#777967] sm:text-[11px]">Total funded value</span>
              </div>
              <div className="px-3 sm:px-5">
                <b className="block text-[20px] font-extrabold tracking-[-0.03em] text-[#20231a] sm:text-[24px]">2,800+</b>
                <span className="mt-1 block text-[10px] font-medium leading-4 text-[#777967] sm:text-[11px]">Active investors</span>
              </div>
              <div className="pl-3 sm:pl-5">
                <b className="block text-[20px] font-extrabold tracking-[-0.03em] text-[#20231a] sm:text-[24px]">8.4%</b>
                <span className="mt-1 block text-[10px] font-medium leading-4 text-[#777967] sm:text-[11px]">Avg. target yield</span>
              </div>
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative min-h-[360px] sm:min-h-[470px] lg:min-h-[520px]">
            <div className="absolute inset-x-[5%] top-[2%] h-[88%] overflow-hidden rounded-[28px] border border-white/80 bg-white/40 p-2 shadow-[0_24px_55px_rgba(38,63,30,0.14)] backdrop-blur-sm sm:inset-x-[8%] sm:top-0">
              <img
                src={HERO_IMAGE}
                alt="Modern investment villa at dusk"
                className="h-full w-full rounded-[21px] object-cover"
              />
              <div className="absolute inset-[8px] rounded-[21px] bg-gradient-to-t from-[#1e2a18]/25 via-transparent to-white/10" />
            </div>

            <div className="absolute bottom-[3%] left-0 hidden h-[150px] w-[42%] overflow-hidden rounded-[22px] border border-white/80 bg-white/60 p-1.5 shadow-[0_18px_40px_rgba(38,63,30,0.16)] backdrop-blur-md sm:block">
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=700&q=80"
                alt="Contemporary property exterior"
                className="h-full w-full rounded-[16px] object-cover"
              />
            </div>

            <div className="absolute left-[3%] top-[10%] rounded-2xl border border-white/70 bg-white/78 px-4 py-3 shadow-[0_14px_35px_rgba(38,63,30,0.15)] backdrop-blur-xl sm:left-0">
              <span className="block text-[10px] font-medium text-[#777967]">Starting investment</span>
              <strong className="mt-0.5 block text-[22px] font-extrabold tracking-[-0.03em] text-[#20231a]">AED 5000</strong>
            </div>

            <div className="absolute right-0 top-[34%] rounded-2xl border border-white/70 bg-white/78 px-4 py-3 shadow-[0_14px_35px_rgba(38,63,30,0.15)] backdrop-blur-xl sm:right-[2%]">
              <span className="block text-[10px] font-medium text-[#777967]">Expected return</span>
              <strong className="mt-0.5 block text-[22px] font-extrabold tracking-[-0.03em] text-[#4c7a2e]">8.5%</strong>
            </div>

            <div className="absolute bottom-[1%] right-[2%] rounded-2xl border border-white/70 bg-[#fdfbf5]/85 px-4 py-3 shadow-[0_14px_35px_rgba(38,63,30,0.15)] backdrop-blur-xl">
              <span className="block text-[10px] font-medium text-[#777967]">Property value</span>
              <strong className="mt-0.5 block text-[22px] font-extrabold tracking-[-0.03em] text-[#20231a]">AED 750K</strong>
            </div>
          </div>
        </div>
      </section>

      <div
        className="
          mb-7
          flex
          flex-col
          gap-3
          rounded-[18px]
          border
          border-white/90
          bg-white/70
          p-3
          shadow-[0_10px_35px_rgba(27,82,108,0.07)]
          backdrop-blur-xl
          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >

        {/* Search */}

        <div className="relative w-full sm:max-w-[360px]">

          <Search
            size={15}
            strokeWidth={2}
            className="
              absolute
              left-3.5
              top-1/2
              -translate-y-1/2
              text-[#8a8b7c]
            "
          />

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search properties..."
            className="
              h-[42px]
              w-full
              rounded-[12px]
              border
              border-[#e5e4d9]
              bg-white/90
              pl-10
              pr-4
              text-[13px]
              font-medium
              text-[#24485b]
              shadow-sm
              outline-none
              transition-all
              duration-300
              placeholder:text-[#a2a39a]
              focus:border-[#8a9463]
              focus:bg-white
              focus:ring-4
              focus:ring-[#6a7847]/[0.08]
            "
          />

        </div>


        <div className="flex gap-2">

          <button
            type="button"
            className="
              flex
              h-[42px]
              items-center
              gap-2
              rounded-[11px]
              border
              border-[#e5e4d9]
              bg-white/90
              px-4
              text-[13px]
              font-bold
              text-[#5f604f]
              shadow-sm
              transition-all
              duration-300
              hover:-translate-y-[1px]
              hover:border-[#73aec6]
              hover:bg-[#faf7ef]
              hover:text-[#4f5b36]
              hover:shadow-md
            "
          >
            <SlidersHorizontal size={13} />
            Filters
          </button>

          <button
            type="button"
            className="
              flex
              h-[42px]
              items-center
              gap-2
              rounded-[11px]
              border
              border-[#e5e4d9]
              bg-white/90
              px-4
              text-[13px]
              font-bold
              text-[#5f604f]
              shadow-sm
              transition-all
              duration-300
              hover:-translate-y-[1px]
              hover:border-[#73aec6]
              hover:bg-[#faf7ef]
              hover:text-[#4f5b36]
              hover:shadow-md
            "
          >
            Highest ROI
            <ChevronDown size={13} />
          </button>

        </div>

      </div>

      <div className="mb-5 flex items-center justify-between px-1">

        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#4a4b3f]">
            Available Opportunities
          </p>

          <p className="mt-1 text-[10px] text-[#4a4b3f]">
            Showing{" "}
            <span className="font-bold text-[#4f5b36]">
              {filteredProperties?.length}
            </span>{" "}
            {activeTab === "my-properties"
              ? "invested properties"
              : "investment properties"}
          </p>
        </div>

        <div
          className="
            hidden
            rounded-full
            border
            border-[#d9eaf2]
            bg-white/70
            px-3
            py-1.5
            text-[8px]
            font-semibold
            text-[#666754]
            shadow-sm
            sm:block
          "
        >
          Real Estate
        </div>

      </div>

      {/* =====================================================
          PROPERTY GRID
      ===================================================== */}

      <div id="properties" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-7">

        {/* ================= LOADING ================= */}

        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="
                animate-pulse
                overflow-hidden
                rounded-[20px]
                border
                border-[#fdfbf5]/80
                bg-white/75
                p-2
                shadow-[0_8px_30px_rgba(20,70,100,0.06)]
              "
            >
              {/* Image */}

              <div className="h-[210px] rounded-[16px] bg-gradient-to-br from-[#e9e8df] to-[#e8e6dc]" />

              {/* Content */}

              <div className="space-y-4 px-3.5 pb-4 pt-4">

                <div className="h-4 w-3/4 rounded-full bg-[#e5e4d9]" />

                <div className="h-2.5 w-1/2 rounded-full bg-[#e9e8df]" />

                <div className="space-y-2">
                  <div className="h-2.5 w-full rounded-full bg-[#e9e8df]" />
                  <div className="h-2.5 w-5/6 rounded-full bg-[#e9e8df]" />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="h-[65px] rounded-[12px] bg-[#eeeee6]" />
                  <div className="h-[65px] rounded-[12px] bg-[#eeeee6]" />
                </div>

                <div className="h-[58px] rounded-[12px] bg-[#eeeee6]" />

                <div className="h-[7px] rounded-full bg-[#e6e5db]" />

                <div className="h-[42px] rounded-[11px] bg-[#e7e5da]" />

              </div>
            </div>
          ))

        ) : filteredProperties?.length > 0 ? (

          filteredProperties?.map((property) => (
            <ScrollReveal>
            <PropertyCard
              key={property?.id}
              property={property}
              onViewInvestment={handleViewInvestment}
              activeTab = {activeTab}
              onSeeDetails = {()=>handleSeeDetails(property?.id)}
            />
            </ScrollReveal>
          ))

        ) : (

          /* ================= EMPTY STATE ================= */

          <div className="col-span-full flex min-h-[380px] items-center justify-center">

            <div
              className="
                flex
                max-w-[460px]
                flex-col
                items-center
                rounded-[24px]
                border
                border-white
                bg-white/70
                px-8
                py-12
                text-center
                shadow-[0_15px_50px_rgba(25,83,110,0.08)]
                backdrop-blur-xl
              "
            >

              <div
                className="
                  flex
                  h-16
                  w-16
                  items-center
                  justify-center
                  rounded-[18px]
                  bg-gradient-to-br
                  from-[#e3ecd6]
                  to-[#e3ecd6]
                  text-[#4f5b36]
                  shadow-inner
                "
              >
                {activeTab === "my-properties" ? (
                  <Wallet
                    size={27}
                    strokeWidth={1.6}
                  />
                ) : (
                  <Building2
                    size={27}
                    strokeWidth={1.6}
                  />
                )}
              </div>

              <h3
                className="
                  mt-5
                  text-[18px]
                  font-bold
                  tracking-[-0.02em]
                  text-[#173f55]
                "
              >
                {search
                  ? "No Properties Found"
                  : activeTab === "my-properties"
                    ? "No Investments Yet"
                    : "No Investment Opportunities"}
              </h3>

              <p
                className="
                  mt-2
                  max-w-[350px]
                  text-[10px]
                  leading-[17px]
                  text-[#6f705f]
                "
              >
                {search
                  ? "We couldn't find any properties matching your search."
                  : activeTab === "my-properties"
                    ? "Properties in which you invest will appear here."
                    : "We couldn't find any properties available for investment at the moment. New opportunities will appear here when available."}
              </p>

            </div>

          </div>
        )}

      </div>
      <ScrollReveal>
     <section
  id="property-categories"
  className="
    relative
    mt-20
    overflow-hidden
  "
>
  {/* ================= MAIN SECTION CARD ================= */}

  <div
    className="
      relative
      w-full max-w-[1800px]
      overflow-hidden
      rounded-[28px]
      border
      border-white/90
      bg-white/45
      px-5
      py-10
      shadow-[0_18px_55px_rgba(27,82,108,0.07)]
      backdrop-blur-xl
      sm:px-8
      sm:py-12
      lg:px-12
      lg:py-14
    "
  >
    {/* ================= SOFT BACKGROUND GLOW ================= */}

    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="
          absolute
          -left-32
          -top-32
          h-[360px]
          w-[360px]
          rounded-full
          bg-[#e6ead9]/45
          blur-[100px]
        "
      />

      <div
        className="
          absolute
          -right-32
          top-[25%]
          h-[420px]
          w-[420px]
          rounded-full
          bg-[#eef0e4]/50
          blur-[110px]
        "
      />

      <div
        className="
          absolute
          bottom-[-200px]
          left-[35%]
          h-[400px]
          w-[400px]
          rounded-full
          bg-[#e4e8d8]/40
          blur-[110px]
        "
      />
    </div>

    {/* ================= CONTENT ================= */}

    <div className="relative z-10">

      {/* ================= HEADING ================= */}

      <div
        className="
          mb-8
          grid
          grid-cols-1
          items-end
          gap-5
          sm:mb-10
          md:grid-cols-[1fr_0.7fr]
          lg:mb-11
        "
      >
        <div>

          <p
            className="
              mb-3
              text-[10px]
              font-bold
              uppercase
              tracking-[0.18em]
              text-[#6a7847]
              sm:text-[11px]
            "
          >
            Explore real estate
          </p>

          <h2
            className="
              max-w-[650px]
              text-[30px]
              font-extrabold
              leading-[1.12]
              tracking-[-0.04em]
              text-[#1f2318]
              sm:text-[36px]
              lg:text-[42px]
            "
          >
            Every kind of property,
            <br />
            one platform
          </h2>

          {/* Small accent */}

          <div
            className="
              mt-4
              h-1
              w-12
              rounded-full
              bg-gradient-to-r
              from-[#4f5b36]
              to-[#8a9463]
            "
          />
        </div>

        <p
          className="
            max-w-[390px]
            pb-1
            text-[11px]
            font-medium
            leading-[1.8]
            text-[#666754]
            sm:text-[12px]
          "
        >
          Diversify across residential, commercial, and development deals
          without managing a single one yourself.
        </p>
      </div>

      {/* ================= PROPERTY CARDS ================= */}

      <div
        className="
          grid
          grid-cols-1
          gap-4
          sm:grid-cols-2
          lg:grid-cols-3
          lg:gap-5
        "
      >
        {propertyCategories.map((property, index) => (
          <button
            key={property.title}
            type="button"
            className="
              group
              relative
              h-[210px]
              overflow-hidden
              rounded-[18px]
              border
              border-white/90
              bg-white
              text-left
              shadow-[0_8px_25px_rgba(27,82,108,0.08)]
              transition-all
              duration-500
              ease-[cubic-bezier(.22,1,.36,1)]
              hover:-translate-y-1
              hover:shadow-[0_20px_40px_rgba(27,82,108,0.16)]
              sm:h-[225px]
            "
          >
            {/* ================= IMAGE ================= */}

            <img
              src={property.image}
              alt={property.title}
              className="
                absolute
                inset-0
                h-full
                w-full
                object-cover
                transition-all
                duration-700
                ease-[cubic-bezier(.22,1,.36,1)]
                group-hover:scale-[1.08]
              "
            />

            {/* ================= IMAGE OVERLAY ================= */}

            <div
              className="
                absolute
                inset-0
                bg-gradient-to-t
                from-[#1f2318]/90
                via-[#1f2318]/25
                to-transparent
                transition-all
                duration-500
                group-hover:from-[#1f2318]/95
                group-hover:via-[#1f2318]/35
              "
            />

            {/* ================= HOVER GLOW ================= */}

            <div
              className="
                absolute
                inset-0
                bg-gradient-to-br
                from-[#8a9463]/0
                via-transparent
                to-[#8a9463]/20
                opacity-0
                transition-opacity
                duration-500
                group-hover:opacity-100
              "
            />

            {/* ================= TOP BADGE ================= */}

            <div
              className="
                absolute
                left-4
                top-4
                rounded-full
                border
                border-white/20
                bg-white/10
                px-2.5
                py-1
                text-[8px]
                font-semibold
                uppercase
                tracking-[0.08em]
                text-white/80
                opacity-0
                backdrop-blur-md
                transition-all
                duration-500
                group-hover:translate-y-0
                group-hover:opacity-100
              "
            >
              Explore
            </div>

            {/* ================= OPPORTUNITY COUNT ================= */}

            <div
              className="
                absolute
                bottom-[43px]
                left-4
              "
            >
              <span
                className="
                  text-[9px]
                  font-medium
                  tracking-[0.02em]
                  text-white/65
                  transition-colors
                  duration-300
                  group-hover:text-white/90
                "
              >
                {property.opportunities}
              </span>
            </div>

            {/* ================= TITLE ================= */}

            <div
              className="
                absolute
                bottom-4
                left-4
                right-4
                flex
                items-center
                justify-between
                gap-3
              "
            >
              <h3
                className="
                  text-[16px]
                  font-bold
                  leading-none
                  tracking-[-0.02em]
                  text-white
                  transition-transform
                  duration-500
                  group-hover:translate-x-1
                  sm:text-[17px]
                "
              >
                {property.title}
              </h3>

              {/* Arrow */}

              <span
                className="
                  flex
                  h-8
                  w-8
                  shrink-0
                  translate-x-3
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-white/35
                  bg-white/10
                  text-[14px]
                  text-white
                  opacity-0
                  backdrop-blur-md
                  transition-all
                  duration-500
                  group-hover:translate-x-0
                  group-hover:opacity-100
                "
              >
                ↗
              </span>
            </div>

            {/* ================= INNER BORDER ================= */}

            <div
              className="
                pointer-events-none
                absolute
                inset-0
                rounded-[18px]
                border
                border-transparent
                transition-all
                duration-500
                group-hover:border-white/50
              "
            />
          </button>
        ))}
      </div>
    </div>
  </div>
</section>
</ScrollReveal>
<ScrollReveal>
<PortfolioOverview />
</ScrollReveal>
<ScrollReveal>
<footer className="relative mt-20 overflow-hidden px-4 pb-6 sm:px-6 lg:px-8">
  {/* ================= FOOTER CARD ================= */}

  <div
    className="
      relative
      mx-auto
      max-w-[1380px]
      overflow-hidden
      rounded-[28px]
      border
      border-white/90
      bg-white/65
      shadow-[0_18px_55px_rgba(27,82,108,0.09)]
      backdrop-blur-xl
    "
  >
    {/* ================= BLURRED BACKGROUND ================= */}

    <div className="pointer-events-none absolute inset-0 overflow-hidden">

      <div
        className="
          absolute
          -left-32
          -top-32
          h-[330px]
          w-[330px]
          rounded-full
          bg-[#e6ead9]/55
          blur-[100px]
        "
      />

      <div
        className="
          absolute
          -right-32
          top-0
          h-[380px]
          w-[380px]
          rounded-full
          bg-[#eceee3]/65
          blur-[110px]
        "
      />

      <div
        className="
          absolute
          bottom-[-220px]
          left-[40%]
          h-[400px]
          w-[400px]
          rounded-full
          bg-[#e3e7d7]/40
          blur-[110px]
        "
      />

      <div className="absolute inset-0 bg-white/20" />

    </div>

    {/* ================= MAIN FOOTER ================= */}

    <div
      className="
        relative
        z-10
        px-6
        py-10
        sm:px-9
        sm:py-12
        lg:px-12
        lg:py-14
      "
    >

      <div
        className="
          grid
          grid-cols-1
          gap-10
          md:grid-cols-[1.35fr_1fr_1fr_1fr_1fr]
          lg:gap-8
        "
      >

        {/* ================= BRAND ================= */}

        <div className="max-w-[250px]">

          <div className="flex items-center gap-3">

            <div
              className="
                flex
                h-9
                w-9
                items-center
                justify-center
                rounded-xl
                bg-gradient-to-br from-[#6fa142] via-[#4c7a2e] to-[#375c20]
                text-white
                shadow-[0_8px_20px_rgba(21,80,109,0.16)]
              "
            >
              <span className="text-[16px]">T</span>
            </div>

            {/* <span
              className="
                text-[18px]
                font-bold
                tracking-[-0.03em]
                text-[#153d54]
              "
            >
              Terra
            </span> */}

          </div>

          <p
            className="
              mt-4
              text-[11px]
              font-medium
              leading-[1.75]
              text-[#666754]
            "
          >
            Fractional real estate investing, built on
            transparency and rigorous underwriting.
          </p>

          <p
            className="
              mt-3
              text-[10px]
              font-medium
              leading-[1.6]
              text-[#9aa9af]
            "
          >
            Invest smarter. Own more.
          </p>

        </div>

        {/* ================= INVESTMENT ================= */}

        <div>

          <h3
            className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.12em]
              text-[#1f2318]
            "
          >
            Investment
          </h3>

          <div className="mt-4 space-y-2.5">

            <a
              href="#properties"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Browse properties
            </a>

            <a
              href="#how-it-works"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              How investing works
            </a>

            <a
              href="#returns"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              View returns
            </a>

          </div>

        </div>

        {/* ================= PROPERTIES ================= */}

        <div>

          <h3
            className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.12em]
              text-[#1f2318]
            "
          >
            Properties
          </h3>

          <div className="mt-4 space-y-2.5">

            <a
              href="#properties"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              All listings
            </a>

            <a
              href="#list-property"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              List a property
            </a>

            <a
              href="#property-categories"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Categories
            </a>

          </div>

        </div>

        {/* ================= RESOURCES ================= */}

        <div>

          <h3
            className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.12em]
              text-[#1f2318]
            "
          >
            Resources
          </h3>

          <div className="mt-4 space-y-2.5">

            <a
              href="#how-it-works"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              How it works
            </a>

            <a
              href="#help"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Help center
            </a>

            <a
              href="#investor-guide"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Investor guide
            </a>

          </div>

        </div>

        {/* ================= COMPANY ================= */}

        <div>

          <h3
            className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.12em]
              text-[#1f2318]
            "
          >
            Company
          </h3>

          <div className="mt-4 space-y-2.5">

            <a
              href="#about"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              About
            </a>

            <a
              href="#careers"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Careers
            </a>

            <a
              href="#contact"
              className="
                block
                text-[11px]
                font-medium
                text-[#666754]
                transition-colors
                duration-300
                hover:text-[#4f5b36]
              "
            >
              Contact
            </a>

          </div>

        </div>

      </div>

      {/* ================= DIVIDER ================= */}

      <div className="my-9 h-px bg-[#dfeaed]" />

      {/* ================= BOTTOM ================= */}

      <div
        className="
          flex
          flex-col
          gap-5
          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >

        <p
          className="
            text-[10px]
            font-medium
            text-[#91a1a7]
          "
        >
          © 2026 Real Estate, Inc. All rights reserved.
        </p>

        <div className="flex items-center gap-5">

          <a
            href="#privacy"
            className="
              text-[10px]
              font-medium
              text-[#82949c]
              transition-colors
              hover:text-[#4f5b36]
            "
          >
            Privacy
          </a>

          <span className="text-[#c4d0d4]">•</span>

          <a
            href="#terms"
            className="
              text-[10px]
              font-medium
              text-[#82949c]
              transition-colors
              hover:text-[#4f5b36]
            "
          >
            Terms
          </a>

        </div>

        {/* ================= SOCIAL ================= */}

        <div className="flex items-center gap-2">

          <a
            href="#"
            aria-label="X"
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              border
              border-[#e5e4d9]
              bg-white/70
              text-[10px]
              font-bold
              text-[#777866]
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:border-[#4f5b36]
              hover:bg-white
              hover:text-[#4f5b36]
            "
          >
            X
          </a>

          <a
            href="#"
            aria-label="LinkedIn"
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              border
              border-[#e5e4d9]
              bg-white/70
              text-[10px]
              font-bold
              text-[#777866]
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:border-[#4f5b36]
              hover:bg-white
              hover:text-[#4f5b36]
            "
          >
            in
          </a>

          <a
            href="#"
            aria-label="Instagram"
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-full
              border
              border-[#e5e4d9]
              bg-white/70
              text-[10px]
              font-bold
              text-[#777866]
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:border-[#4f5b36]
              hover:bg-white
              hover:text-[#4f5b36]
            "
          >
            ◎
          </a>

        </div>

      </div>

    </div>
  </div>
</footer>
</ScrollReveal>
    </main>
}
    <ProfileModal
      isOpen={isProfileModalOpen}
      onClose={() => {
        setIsProfileModalOpen(false);
        // setProfile(null);
      }}
      profile={profile}
      isLoading={isProfileLoading}
      onUpdateProfile={handleUpdateProfile}
    />

  </div>
);

};

export default Home;